import React from 'react';
import { Star, Tv2 } from 'lucide-react';
import TrendingReasonChip from '../trending/TrendingReasonChip';
import { flagImgUrl, flagImgSrcSet } from '../../utils/regions';

const ShowHero = ({ show, region, regionLabel }) => {
  const runtime = (show.episode_run_time && show.episode_run_time[0]) || null;
  const flagUrl = flagImgUrl(region);
  const flagSrcSet = flagImgSrcSet(region);
  return (
    <header className="show-hero" data-testid="show-hero">
      {show.backdrop_url && (
        <div
          className="hero-backdrop"
          style={{ backgroundImage: `url(${show.backdrop_url})` }}
          aria-hidden="true"
        />
      )}
      <div className="hero-overlay" aria-hidden="true" />
      <div className="hero-inner">
        <div className="poster-wrap">
          {show.image_url ? (
            <img src={show.image_url} alt={`${show.title} poster`} loading="lazy" />
          ) : (
            <div className="poster-fallback">No poster</div>
          )}
        </div>
        <div className="hero-body">
          <span className="hero-eyebrow">
            <Tv2 size={14} /> TV Series
          </span>
          <h1 data-testid="show-title">{show.title}</h1>
          {show.tagline && <p className="tagline">"{show.tagline}"</p>}

          <div className="meta-row">
            {show.year && <span className="meta-pill">{show.year}</span>}
            {show.number_of_seasons ? (
              <span className="meta-pill">
                {show.number_of_seasons} season{show.number_of_seasons !== 1 ? 's' : ''}
              </span>
            ) : null}
            {runtime ? <span className="meta-pill">{runtime} min</span> : null}
            {show.status ? <span className="meta-pill status">{show.status}</span> : null}
            {show.vote_average ? (
              <span className="meta-pill score">
                <Star size={13} /> {show.vote_average.toFixed(1)}
                {show.vote_count ? (
                  <span className="vote-count">({show.vote_count.toLocaleString()})</span>
                ) : null}
              </span>
            ) : null}
          </div>

          {show.trending_reason && (
            <div className="reason-wrap" data-testid="show-reason">
              <TrendingReasonChip reason={show.trending_reason} size="lg" />
            </div>
          )}

          {show.genres && show.genres.length > 0 && (
            <div className="genres" data-testid="show-genres">
              {show.genres.map((g) => (
                <span key={g.id} className="genre-chip">{g.name}</span>
              ))}
            </div>
          )}

          {regionLabel && (
            <div className="region-line" data-testid="show-region-line">
              Showing details for{' '}
              {flagUrl ? (
                <img
                  className="region-flag-img"
                  src={flagUrl}
                  srcSet={flagSrcSet}
                  alt={`${regionLabel} flag`}
                  width="22"
                  height="14"
                  loading="lazy"
                />
              ) : null}
              <strong>{regionLabel}</strong>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .show-hero {
          position: relative;
          padding: 4.5rem 1.5rem 3rem;
          overflow: hidden;
          background: #0a0a0a;
          border-bottom: 1px solid #161616;
          min-height: 460px;
        }
        .hero-backdrop {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center top;
          opacity: 0.4;
          filter: saturate(0.8);
        }
        .hero-overlay {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(180deg, rgba(10, 10, 10, 0.4) 0%, rgba(10, 10, 10, 0.92) 75%, #0a0a0a 100%),
            radial-gradient(1000px 480px at 20% 0%, rgba(230, 47, 136, 0.18) 0%, rgba(230, 47, 136, 0) 60%);
        }
        .hero-inner {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 220px 1fr;
          gap: 2.5rem;
          align-items: flex-end;
        }
        .poster-wrap {
          aspect-ratio: 2 / 3;
          width: 220px;
          border-radius: 14px;
          overflow: hidden;
          background: #0a0a0a;
          box-shadow: 0 30px 80px -20px rgba(0, 0, 0, 0.8);
        }
        .poster-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .poster-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #555;
          font-size: 0.85rem;
        }
        .hero-body {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
        }
        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          color: #E62F88;
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.22em;
          text-transform: uppercase;
        }
        h1 {
          font-size: clamp(2rem, 4vw, 3.25rem);
          color: #fff;
          font-weight: 900;
          letter-spacing: -0.02em;
          line-height: 1.05;
          margin: 0;
        }
        .tagline {
          color: #a1a1aa;
          font-style: italic;
          font-size: 1rem;
          margin: 0;
        }
        .meta-row {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .meta-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          background: rgba(20, 20, 20, 0.85);
          border: 1px solid #2a2a2a;
          color: #d4d4d8;
          padding: 0.3rem 0.7rem;
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 600;
        }
        .meta-pill.status {
          color: #93c5fd;
          border-color: rgba(96, 165, 250, 0.35);
          background: rgba(96, 165, 250, 0.1);
        }
        .meta-pill.score {
          color: #facc15;
        }
        .vote-count {
          color: #71717a;
          font-weight: 500;
        }
        .reason-wrap {
          margin-top: 0.1rem;
        }
        .genres {
          display: flex;
          gap: 0.4rem;
          flex-wrap: wrap;
        }
        .genre-chip {
          color: #a1a1aa;
          font-size: 0.78rem;
          font-weight: 600;
          padding: 0.25rem 0.65rem;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.06);
        }
        .region-line {
          color: #8a8a93;
          font-size: 0.85rem;
          margin-top: 0.5rem;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
        }
        .region-line strong {
          color: #fff;
          font-weight: 700;
        }
        :global(.region-flag-img) {
          display: inline-block;
          border-radius: 2px;
          box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.06);
          vertical-align: middle;
        }
        @media (max-width: 720px) {
          .show-hero {
            padding: 3rem 1.25rem 2rem;
          }
          .hero-inner {
            grid-template-columns: 130px 1fr;
            gap: 1.25rem;
            align-items: flex-start;
          }
          .poster-wrap {
            width: 130px;
            border-radius: 10px;
          }
          h1 {
            font-size: 1.6rem;
          }
        }
      `}</style>
    </header>
  );
};

export default ShowHero;
